import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayoutApple, ButtonApple, BadgeApple } from '@/components/apple-ui';
import {
  Plus, Search, FolderOpen, MoreVertical, FileText, Calendar, Trash2, Edit,
  ExternalLink, ChevronDown, ChevronUp, X, Loader2, FileUp, File, BookOpen,
  ArrowRight, Briefcase, Clock, CheckCircle2, PlayCircle, XCircle,
  ChevronLeft, ChevronRight as ChevRight, ArrowUpDown, ArrowUp, ArrowDown,
  Archive, Eye,
} from 'lucide-react';
import JobsModal from './JobsModal';
import { toast } from 'sonner';
import PDFDisclaimer from '@/components/PDFDisclaimer';
import '@/styles/apple-design-system.css';
import { useTheme } from '@/contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LANGUAGES = [
  { code: 'en', name: 'English' }, { code: 'hi', name: 'Hindi' },
  { code: 'gu', name: 'Gujarati' }, { code: 'ta', name: 'Tamil' },
  { code: 'te', name: 'Telugu' }, { code: 'pa', name: 'Punjabi' },
  { code: 'mr', name: 'Marathi' }, { code: 'kn', name: 'Kannada' },
  { code: 'ml', name: 'Malayalam' }, { code: 'bn', name: 'Bengali' },
  { code: 'or', name: 'Odia' }, { code: 'as', name: 'Assamese' },
  { code: 'ur', name: 'Urdu' }, { code: 'de', name: 'German' },
  { code: 'fr', name: 'French' }, { code: 'es', name: 'Spanish' },
];

const PREBUILT_INSTRUCTIONS = [
  'Maintain formal tone throughout', 'Preserve brand names and product names',
  'Keep technical terms in English', 'Use gender-neutral language',
  'Adapt idioms and cultural references', 'Maintain consistent terminology',
  'Preserve formatting and structure', 'Use industry-specific terminology',
  'Keep numbers and dates in original format',
];

const SUPPORTED_EXTENSIONS = ['docx', 'pdf', 'txt', 'xlsx'];

const JOB_STATUS_CFG = {
  new:         { label: 'New',         cls: 'bg-blue-50 text-blue-700',    Icon: Clock },
  accepted:    { label: 'Accepted',    cls: 'bg-indigo-50 text-indigo-700', Icon: PlayCircle },
  in_progress: { label: 'In Progress', cls: 'bg-amber-50 text-amber-700',  Icon: PlayCircle },
  completed:   { label: 'Completed',   cls: 'bg-green-50 text-green-700',  Icon: CheckCircle2 },
  delivered:   { label: 'Delivered',   cls: 'bg-purple-50 text-purple-700', Icon: CheckCircle2 },
  declined:    { label: 'Declined',    cls: 'bg-red-50 text-red-700',      Icon: XCircle },
};

const STATUS_FILTERS = [
  { key: 'all',         label: 'All' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'completed',   label: 'Completed' },
  { key: 'archived',    label: 'Archived' },
];

const PAGE_SIZE = 20;

const getLangName = (code) => {
  const l = LANGUAGES.find(l => l.code === code || l.name.toLowerCase() === code?.toLowerCase());
  return l ? l.name : (code || '—');
};

const pid = (p) => p.project_id || p.id;

function deadlineInfo(deadline) {
  if (!deadline) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(deadline);
  const days = Math.round((due - today) / 86400000);
  if (days < 0)  return { label: `${Math.abs(days)}d overdue`, cls: 'text-red-600 font-semibold' };
  if (days <= 3) return { label: `${days}d left`,              cls: 'text-amber-600 font-semibold' };
  return { label: due.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', timeZone: 'Asia/Kolkata' }), cls: 'text-gray-600' };
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const ProjectsPageApple = ({ user, planInfo, onLogout }) => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [projects, setProjects]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProject, setEditingProject]   = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [jobsProject, setJobsProject] = useState(null);
  const [myJobs, setMyJobs]         = useState([]);
  const [myJobsLoading, setMyJobsLoading] = useState(false);
  const [showMyJobs, setShowMyJobs] = useState(true);

  // Table state
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortKey, setSortKey]   = useState('updated_at');
  const [sortDir, setSortDir]   = useState('desc');
  const [page, setPage]         = useState(1);
  const [selected, setSelected] = useState(new Set());

  const isLinguistOrReviewer = user?.role === 'translator' || user?.role === 'reviewer' || user?.role === 'member';

  const fetchMyJobs = useCallback(async () => {
    setMyJobsLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/jobs`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setMyJobs(await res.json());
    } catch { /* non-critical */ }
    setMyJobsLoading(false);
  }, []);

  useEffect(() => { fetchMyJobs(); }, [fetchMyJobs]);
  useEffect(() => { fetchProjects(); }, [showArchived]);

  const fetchProjects = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/projects?show_archived=${showArchived}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) { onLogout?.(); return; }
      if (!res.ok) { toast.error('Failed to load projects'); return; }
      const data = await res.json();
      setProjects(Array.isArray(data) ? data : (Array.isArray(data.projects) ? data.projects : []));
    } catch { toast.error('Failed to load projects'); }
    finally { setLoading(false); }
  };

  const archiveProject = async (projectId, archive) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ archived: archive }),
      });
      if (res.ok) {
        setProjects(prev => prev.filter(p => pid(p) !== projectId));
        toast.success(archive ? 'Project archived' : 'Project unarchived');
      } else toast.error('Failed to update project');
    } catch { toast.error('Failed to update project'); }
    setActiveMenu(null);
  };

  const deleteProject = async (projectId) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setProjects(prev => prev.filter(p => pid(p) !== projectId));
        toast.success('Project deleted');
      } else toast.error('Failed to delete project');
    } catch { toast.error('Failed to delete project'); }
    setActiveMenu(null);
    setDeleteTarget(null);
  };

  const handleBulkArchive = async () => {
    const token = localStorage.getItem('token');
    const ids = Array.from(selected);
    try {
      const res = await fetch(`${API_URL}/api/projects/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ project_ids: ids, action: 'archive' }),
      });
      if (res.ok) {
        setProjects(prev => prev.filter(p => !selected.has(pid(p))));
        setSelected(new Set());
        toast.success(`${ids.length} project(s) archived`);
      }
    } catch { toast.error('Bulk archive failed'); }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} project(s)? This cannot be undone.`)) return;
    const token = localStorage.getItem('token');
    const ids = Array.from(selected);
    try {
      const res = await fetch(`${API_URL}/api/projects/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ project_ids: ids, action: 'delete' }),
      });
      if (res.ok) {
        setProjects(prev => prev.filter(p => !selected.has(pid(p))));
        setSelected(new Set());
        toast.success(`${ids.length} project(s) deleted`);
      }
    } catch { toast.error('Bulk delete failed'); }
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
    setPage(1);
  };

  // Filter + sort + paginate
  const filtered = projects.filter(p => {
    const q = searchQuery.toLowerCase();
    if (q && !p.name?.toLowerCase().includes(q)) return false;
    if (statusFilter === 'archived') return p.archived;
    if (statusFilter === 'in_progress') return p.status === 'in_progress' && !p.archived;
    if (statusFilter === 'completed') return p.status === 'completed' && !p.archived;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    let va, vb;
    if (sortKey === 'name')       { va = a.name || ''; vb = b.name || ''; }
    else if (sortKey === 'progress') { va = a.progress || 0; vb = b.progress || 0; }
    else if (sortKey === 'deadline') {
      va = a.deadline ? new Date(a.deadline).getTime() : Infinity;
      vb = b.deadline ? new Date(b.deadline).getTime() : Infinity;
    }
    else { va = new Date(a.updated_at || a.created_at || 0); vb = new Date(b.updated_at || b.created_at || 0); }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paginated = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Recent = top 3 by updated_at (not archived)
  const recent = [...projects]
    .filter(p => !p.archived)
    .sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0))
    .slice(0, 4);

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown size={12} className="text-gray-300 ml-1" />;
    return sortDir === 'asc'
      ? <ArrowUp size={12} className="text-blue-500 ml-1" />
      : <ArrowDown size={12} className="text-blue-500 ml-1" />;
  };

  const th = `px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 cursor-pointer select-none whitespace-nowrap hover:text-gray-700 transition-colors`;
  const td = `px-4 py-3 text-sm align-middle`;

  return (
    <MainLayoutApple user={user} planInfo={planInfo} onLogout={onLogout}>
      <div className="apple-content" data-testid="apple-projects-page">

        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="apple-content-title">Projects</h1>
            <p className="apple-content-subtitle">{projects.length} project{projects.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            {selected.size > 0 && (
              <>
                <span className="text-sm text-gray-500">{selected.size} selected</span>
                <button onClick={handleBulkArchive} className="flex items-center gap-1.5 px-3 py-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl hover:bg-amber-100">
                  <Archive size={14} /> Archive
                </button>
                <button onClick={handleBulkDelete} className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100">
                  <Trash2 size={14} /> Delete
                </button>
                <button onClick={() => setSelected(new Set())} className="p-2 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-100"><X size={16} /></button>
              </>
            )}
            <ButtonApple icon={Plus} onClick={() => setShowCreateModal(true)} data-testid="create-project-btn">
              New Project
            </ButtonApple>
          </div>
        </div>

        {/* My Assignments — active jobs */}
        {myJobs.length > 0 && (
          <div className={`mb-6 rounded-2xl border p-4 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-blue-50/50 border-blue-100'}`}>
            <button onClick={() => setShowMyJobs(v => !v)} className="flex items-center gap-2 mb-3 w-full">
              <Briefcase size={16} className="text-blue-600" />
              <span className="text-sm font-semibold text-gray-900">My Assignments</span>
              <span className="px-2 py-0.5 text-xs font-semibold bg-blue-600 text-white rounded-full ml-1">
                {myJobs.filter(j => j.status === 'new' || j.status === 'accepted').length}
              </span>
              {showMyJobs ? <ChevronUp size={14} className="text-gray-400 ml-auto" /> : <ChevronDown size={14} className="text-gray-400 ml-auto" />}
            </button>
            {showMyJobs && !myJobsLoading && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {myJobs.slice(0, 6).map(job => {
                  const cfg = JOB_STATUS_CFG[job.status] || JOB_STATUS_CFG.new;
                  const Icon = cfg.Icon;
                  const doAction = async (action) => {
                    const token = localStorage.getItem('token');
                    const res = await fetch(`${API_URL}/api/jobs/${job.job_id}/${action}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
                    if (res.ok) fetchMyJobs();
                  };
                  const today = new Date(); today.setHours(0,0,0,0);
                  const due = job.deadline ? new Date(job.deadline) : null;
                  const daysLeft = due ? Math.round((due - today) / 86400000) : null;
                  return (
                    <div key={job.job_id} className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${['bg-blue-50 text-blue-700','bg-orange-50 text-orange-700','bg-purple-50 text-purple-700','bg-green-50 text-green-700'][(job.workflow_step-1)%4]}`}>
                          {job.step_name}
                        </span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full flex-shrink-0 ${cfg.cls}`}>
                          <Icon size={10} />{cfg.label}
                        </span>
                      </div>
                      <p className="font-semibold text-sm text-gray-900 truncate mb-1">{job.file_name}</p>
                      <p className="text-xs text-gray-500 truncate mb-2">{job.project_name}</p>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          {job.word_count > 0 && <span>{job.word_count.toLocaleString()} words</span>}
                          {daysLeft !== null && (
                            <span className={daysLeft < 0 ? 'text-red-600 font-medium' : daysLeft <= 2 ? 'text-amber-600 font-medium' : ''}>
                              {daysLeft < 0 ? `${Math.abs(daysLeft)}d overdue` : `${daysLeft}d left`}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-1">
                          {job.status === 'new' && (
                            <>
                              <button onClick={() => doAction('accept')} className="px-2 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded font-medium">Accept</button>
                              <button onClick={() => doAction('decline')} className="px-2 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-700 rounded font-medium">Decline</button>
                            </>
                          )}
                          {(job.status === 'accepted' || job.status === 'in_progress') && (
                            <button onClick={() => doAction('complete')} className="px-2 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded font-medium">Complete</button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Recent Projects */}
        {!loading && recent.length > 0 && !searchQuery && statusFilter === 'all' && (
          <div className="mb-6">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Recent Projects</p>
            <div className="flex flex-wrap gap-2">
              {recent.map(p => (
                <button
                  key={pid(p)}
                  onClick={() => navigate(`/projects/${pid(p)}`)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium transition-colors ${
                    isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-200 hover:bg-gray-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300'
                  }`}
                >
                  <FolderOpen size={14} className="text-blue-500" />
                  {p.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Filters row */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setPage(1); }}
              className="apple-input pl-9 py-2 text-sm"
              data-testid="search-projects-input"
            />
          </div>

          {/* Status chips */}
          <div className="flex flex-wrap gap-1.5">
            {STATUS_FILTERS.map(f => (
              <button
                key={f.key}
                onClick={() => { setStatusFilter(f.key); setPage(1); }}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
                  statusFilter === f.key
                    ? 'bg-blue-600 text-white border-blue-600'
                    : isDarkMode ? 'bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {f.label}
                <span className="ml-1.5 opacity-70">
                  {f.key === 'all' ? projects.length
                    : f.key === 'archived' ? projects.filter(p => p.archived).length
                    : projects.filter(p => p.status === f.key && !p.archived).length}
                </span>
              </button>
            ))}
          </div>

          {/* Archived toggle */}
          <button
            onClick={() => { setShowArchived(v => !v); setPage(1); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-medium transition-colors ${
              showArchived ? 'bg-amber-50 text-amber-700 border-amber-200' : isDarkMode ? 'bg-gray-800 text-gray-400 border-gray-700 hover:bg-gray-700' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
            }`}
            data-testid="show-archived-toggle"
          >
            <Archive size={13} />
            {showArchived ? 'Hide Archived' : 'Archived'}
          </button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-blue-600" size={32} /></div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 rounded-3xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <FolderOpen className="text-gray-400" size={36} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {searchQuery || statusFilter !== 'all' ? 'No matching projects' : 'No projects yet'}
            </h3>
            <p className="text-gray-500 mb-6 text-sm">
              {searchQuery || statusFilter !== 'all' ? 'Try adjusting filters' : 'Create your first project to get started'}
            </p>
            {!searchQuery && statusFilter === 'all' && (
              <ButtonApple icon={Plus} onClick={() => setShowCreateModal(true)}>Create Project</ButtonApple>
            )}
          </div>
        ) : (
          <div className={`rounded-2xl border overflow-hidden ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse">
                <thead className={`${isDarkMode ? 'bg-gray-800 border-b border-gray-700' : 'bg-gray-50 border-b border-gray-200'}`}>
                  <tr>
                    <th className="pl-4 pr-2 py-3">
                      <input
                        type="checkbox"
                        checked={selected.size === paginated.length && paginated.length > 0}
                        onChange={() => {
                          if (selected.size === paginated.length) setSelected(new Set());
                          else setSelected(new Set(paginated.map(p => pid(p))));
                        }}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600"
                      />
                    </th>
                    <th className={`${th} w-10`}>#</th>
                    <th className={th} onClick={() => toggleSort('name')}>
                      <span className="flex items-center">Project Name <SortIcon col="name" /></span>
                    </th>
                    <th className={`${th} min-w-[120px]`} onClick={() => toggleSort('progress')}>
                      <span className="flex items-center">Progress <SortIcon col="progress" /></span>
                    </th>
                    <th className={th}>Languages</th>
                    <th className={th}>Status</th>
                    <th className={`${th}`} onClick={() => toggleSort('deadline')}>
                      <span className="flex items-center">Deadline <SortIcon col="deadline" /></span>
                    </th>
                    <th className={`${th}`} onClick={() => toggleSort('updated_at')}>
                      <span className="flex items-center">Updated <SortIcon col="updated_at" /></span>
                    </th>
                    <th className={`${th} w-10`}></th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-100'}`}>
                  {paginated.map((project, idx) => {
                    const progress = project.progress || 0;
                    const isCompleted = project.status === 'completed' || progress === 100;
                    const dl = deadlineInfo(project.deadline);
                    const rowNum = (page - 1) * PAGE_SIZE + idx + 1;
                    const isSelected = selected.has(pid(project));
                    const isMenuOpen = activeMenu === pid(project);

                    return (
                      <tr
                        key={pid(project)}
                        className={`transition-colors ${
                          isSelected
                            ? isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50'
                            : isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50'
                        }`}
                        data-testid={`project-row-${pid(project)}`}
                      >
                        {/* Checkbox */}
                        <td className="pl-4 pr-2 py-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => setSelected(prev => {
                              const next = new Set(prev);
                              isSelected ? next.delete(pid(project)) : next.add(pid(project));
                              return next;
                            })}
                            onClick={e => e.stopPropagation()}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600"
                          />
                        </td>

                        {/* Row number */}
                        <td className={`${td} text-gray-400 text-xs`}>{rowNum}</td>

                        {/* Project Name */}
                        <td className={`${td} max-w-[240px]`}>
                          <button
                            className="flex flex-col items-start text-left w-full"
                            onClick={() => navigate(`/projects/${pid(project)}`)}
                          >
                            <span className={`font-semibold truncate block w-full hover:text-blue-600 transition-colors ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                              {project.name}
                            </span>
                            {project.archived && (
                              <span className="text-[10px] text-gray-400 mt-0.5">Archived</span>
                            )}
                            {project.instruction_name && (
                              <span className="text-[10px] text-gray-400 mt-0.5 truncate block w-full">{project.instruction_name}</span>
                            )}
                          </button>
                        </td>

                        {/* Progress */}
                        <td className={`${td} min-w-[120px]`}>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${isCompleted ? 'bg-green-500' : progress >= 60 ? 'bg-blue-500' : progress >= 30 ? 'bg-amber-500' : 'bg-gray-300'}`}
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                            <span className={`text-xs font-semibold w-8 text-right flex-shrink-0 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{progress}%</span>
                          </div>
                        </td>

                        {/* Languages */}
                        <td className={td}>
                          <div className="flex items-center gap-1 flex-wrap">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isDarkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                              {getLangName(project.source_lang || project.source_language)}
                            </span>
                            <ArrowRight size={10} className="text-gray-300 flex-shrink-0" />
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isDarkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-50 text-purple-700'}`}>
                              {getLangName(project.target_lang || project.target_language)}
                            </span>
                          </div>
                        </td>

                        {/* Status */}
                        <td className={td}>
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full ${
                            project.archived ? 'bg-gray-100 text-gray-500'
                            : isCompleted ? 'bg-green-50 text-green-700'
                            : 'bg-blue-50 text-blue-700'
                          }`}>
                            {project.archived ? 'Archived' : isCompleted ? 'Completed' : 'In Progress'}
                          </span>
                        </td>

                        {/* Deadline */}
                        <td className={`${td} whitespace-nowrap`}>
                          {dl ? (
                            <span className={`text-xs ${dl.cls}`}>{dl.label}</span>
                          ) : (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </td>

                        {/* Updated */}
                        <td className={`${td} whitespace-nowrap text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {project.updated_at
                            ? new Date(project.updated_at).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', timeZone: 'Asia/Kolkata' })
                            : '—'}
                        </td>

                        {/* Actions */}
                        <td className={`${td} relative`}>
                          <button
                            onClick={(e) => { e.stopPropagation(); setActiveMenu(isMenuOpen ? null : pid(project)); }}
                            className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-400 hover:bg-gray-100'}`}
                            data-testid={`project-menu-btn-${pid(project)}`}
                          >
                            <MoreVertical size={16} />
                          </button>
                          {isMenuOpen && (
                            <div className={`absolute right-8 top-0 w-52 border rounded-2xl shadow-xl z-50 overflow-hidden ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`} style={{ marginTop: 0 }}>
                              <button onClick={() => { navigate(`/projects/${pid(project)}`); setActiveMenu(null); }} className={`flex items-center gap-3 w-full px-4 py-3 text-sm transition-colors ${isDarkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-700 hover:bg-gray-50'}`}>
                                <Eye size={15} className="text-gray-400" /> View Details
                              </button>
                              <button onClick={() => { navigate(`/cat?project=${pid(project)}`); setActiveMenu(null); }} className={`flex items-center gap-3 w-full px-4 py-3 text-sm transition-colors ${isDarkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-700 hover:bg-gray-50'}`}>
                                <ExternalLink size={15} className="text-gray-400" /> Open in CAT
                              </button>
                              <button onClick={() => { setJobsProject(project); setActiveMenu(null); }} className={`flex items-center gap-3 w-full px-4 py-3 text-sm transition-colors ${isDarkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-700 hover:bg-gray-50'}`}>
                                <Briefcase size={15} className="text-blue-400" /> Manage Jobs
                              </button>
                              <button onClick={() => { setEditingProject(project); setShowCreateModal(true); setActiveMenu(null); }} className={`flex items-center gap-3 w-full px-4 py-3 text-sm transition-colors ${isDarkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-700 hover:bg-gray-50'}`}>
                                <Edit size={15} className="text-gray-400" /> Edit
                              </button>
                              <button onClick={() => { archiveProject(pid(project), !project.archived); }} className={`flex items-center gap-3 w-full px-4 py-3 text-sm transition-colors ${isDarkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-700 hover:bg-gray-50'}`}>
                                <Archive size={15} className="text-gray-400" /> {project.archived ? 'Unarchive' : 'Archive'}
                              </button>
                              <button onClick={() => { setDeleteTarget(pid(project)); setActiveMenu(null); }} className={`flex items-center gap-3 w-full px-4 py-3 text-sm text-red-600 transition-colors ${isDarkMode ? 'hover:bg-red-900/20' : 'hover:bg-red-50'}`}>
                                <Trash2 size={15} /> Delete
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className={`flex items-center justify-between px-4 py-3 border-t text-sm ${isDarkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
                <span>{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, sorted.length)} of {sorted.length}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 transition-colors">
                    <ChevronLeft size={16} />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1).map((n, i, arr) => (
                    <React.Fragment key={n}>
                      {i > 0 && arr[i - 1] !== n - 1 && <span className="px-1">…</span>}
                      <button
                        onClick={() => setPage(n)}
                        className={`w-8 h-8 rounded-lg text-xs font-semibold transition-colors ${n === page ? 'bg-blue-600 text-white' : isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                      >{n}</button>
                    </React.Fragment>
                  ))}
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 transition-colors">
                    <ChevRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeMenu && <div className="fixed inset-0 z-40" onClick={() => setActiveMenu(null)} />}
      </div>

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm mx-4 p-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center flex-shrink-0">
                <Trash2 size={22} className="text-red-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Project</h3>
                <p className="text-sm text-gray-500 mt-0.5">This action cannot be undone.</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
              <ButtonApple variant="ghost" onClick={() => setDeleteTarget(null)}>Cancel</ButtonApple>
              <button onClick={() => deleteProject(deleteTarget)} className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-2xl transition-colors">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {jobsProject && <JobsModal project={jobsProject} user={user} onClose={() => setJobsProject(null)} />}

      {showCreateModal && (
        <ProjectModalApple
          project={editingProject}
          onClose={() => { setShowCreateModal(false); setEditingProject(null); }}
          onSave={(saved) => {
            if (editingProject) {
              setProjects(prev => prev.map(p => pid(p) === pid(saved) ? saved : p));
            } else {
              setProjects(prev => [saved, ...prev]);
            }
            setShowCreateModal(false);
            setEditingProject(null);
          }}
        />
      )}
    </MainLayoutApple>
  );
};

// ─── Project Modal (unchanged from original) ─────────────────────────────────

const ProjectModalApple = ({ project, onClose, onSave }) => {
  const fileInputRef = useRef(null);
  const DEFAULT_STEPS = [{ step: 1, name: 'Translation' }, { step: 2, name: 'Review' }];
  const [formData, setFormData] = useState({
    name: project?.name || '',
    instruction_name: project?.instruction_name || '',
    source_lang: project?.source_lang || project?.source_language || 'en',
    target_lang: project?.target_lang || project?.target_language || 'hi',
    prebuilt_instructions: project?.prebuilt_instructions || [],
    custom_instructions: project?.custom_instructions || '',
    deadline: project?.deadline || '',
    workflow_steps: project?.workflow_steps || DEFAULT_STEPS,
  });
  const [instructionFiles, setInstructionFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);
  const [showPDFDisclaimer, setShowPDFDisclaimer] = useState(false);
  const [pendingPDFFiles, setPendingPDFFiles] = useState([]);
  const [pendingNonPDFFiles, setPendingNonPDFFiles] = useState([]);

  const handleInstructionToggle = (instruction) => {
    setFormData(prev => {
      const current = prev.prebuilt_instructions;
      if (current.includes(instruction)) return { ...prev, prebuilt_instructions: current.filter(i => i !== instruction) };
      return { ...prev, prebuilt_instructions: [...current, instruction] };
    });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const valid = [], pdfs = [], skipped = [];
    files.forEach(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      if (SUPPORTED_EXTENSIONS.includes(ext)) { ext === 'pdf' ? pdfs.push(file) : valid.push(file); }
      else skipped.push(file);
    });
    if (skipped.length) toast.warning(`${skipped.length} file(s) skipped. Supported: DOCX, PDF, TXT, XLSX`);
    if (pdfs.length) { setPendingPDFFiles(pdfs); setPendingNonPDFFiles(valid); setShowPDFDisclaimer(true); return; }
    if (valid.length) setInstructionFiles(prev => [...prev, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePDFProceed = () => {
    setInstructionFiles(prev => [...prev, ...pendingNonPDFFiles, ...pendingPDFFiles]);
    setShowPDFDisclaimer(false); setPendingPDFFiles([]); setPendingNonPDFFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const handlePDFCancel = () => {
    if (pendingNonPDFFiles.length) setInstructionFiles(prev => [...prev, ...pendingNonPDFFiles]);
    setShowPDFDisclaimer(false); setPendingPDFFiles([]); setPendingNonPDFFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (ext === 'pdf')  return <File className="h-4 w-4 text-red-500" />;
    if (ext === 'docx') return <FileText className="h-4 w-4 text-blue-500" />;
    if (ext === 'xlsx') return <FileText className="h-4 w-4 text-green-500" />;
    return <FileText className="h-4 w-4 text-gray-500" />;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error('Project name is required'); return; }
    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      let response;
      const body = {
        name: formData.name, instruction_name: formData.instruction_name,
        source_lang: formData.source_lang, target_lang: formData.target_lang,
        prebuilt_instructions: formData.prebuilt_instructions,
        custom_instructions: formData.custom_instructions,
        deadline: formData.deadline || null, workflow_steps: formData.workflow_steps,
      };

      if (project) {
        response = await fetch(`${API_URL}/api/projects/${pid(project)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
        });
      } else if (instructionFiles.length > 0) {
        const fd = new FormData();
        Object.entries(body).forEach(([k, v]) => {
          if (v !== null && v !== undefined) fd.append(k, Array.isArray(v) ? JSON.stringify(v) : String(v));
        });
        instructionFiles.forEach(file => fd.append('instruction_files', file));
        response = await fetch(`${API_URL}/api/projects/with-document`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      } else {
        response = await fetch(`${API_URL}/api/projects`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
        });
      }

      const data = await response.json();
      if (response.ok) { toast.success(project ? 'Project updated' : 'Project created'); onSave(data); }
      else toast.error(data.detail || 'Failed to save project');
    } catch { toast.error('Failed to save project'); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="project-modal">
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-8 py-6 border-b border-gray-100 flex-shrink-0">
            <h2 className="text-xl font-semibold text-gray-900">{project ? 'Edit Project' : 'Create Project'}</h2>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors" data-testid="close-modal-btn"><X size={20} /></button>
          </div>
          <form onSubmit={handleSubmit} className="p-8 overflow-y-auto flex-1">
            <div className="apple-input-group mb-6">
              <label className="apple-label">Project Name *</label>
              <input type="text" value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} className="apple-input" placeholder="e.g., Marketing Website Translation" data-testid="project-name-input" autoFocus />
            </div>
            <div className="apple-input-group mb-6">
              <label className="apple-label">Instruction Name</label>
              <input type="text" value={formData.instruction_name || ''} onChange={e => setFormData(p => ({ ...p, instruction_name: e.target.value }))} className="apple-input" placeholder="e.g., Formal Marketing Style" data-testid="instruction-name-input" />
            </div>
            <div className="apple-input-group mb-6">
              <label className="apple-label">Deadline</label>
              <input type="date" value={formData.deadline || ''} onChange={e => setFormData(p => ({ ...p, deadline: e.target.value }))} className="apple-input" data-testid="deadline-input" />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="apple-input-group">
                <label className="apple-label">Source Language</label>
                <select value={formData.source_lang} onChange={e => setFormData(p => ({ ...p, source_lang: e.target.value }))} className="apple-input apple-select" data-testid="source-language-select">
                  {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
                </select>
              </div>
              <div className="apple-input-group">
                <label className="apple-label">Target Language</label>
                <select value={formData.target_lang} onChange={e => setFormData(p => ({ ...p, target_lang: e.target.value }))} className="apple-input apple-select" data-testid="target-language-select">
                  {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
                </select>
              </div>
            </div>

            {/* Instruction docs — new project only */}
            {!project && (
              <div className="apple-input-group mb-6">
                <label className="apple-label flex items-center gap-2"><FileUp size={16} /> Instruction Documents <span className="text-xs text-gray-400 font-normal">(optional)</span></label>
                <p className="text-xs text-gray-500 mb-3">Upload style guides, glossaries, or reference docs</p>
                <div className="border-2 border-dashed border-gray-200 rounded-2xl p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-all" onClick={() => fileInputRef.current?.click()} data-testid="instruction-upload-zone">
                  <input type="file" accept=".docx,.pdf,.txt,.xlsx" onChange={handleFileChange} ref={fileInputRef} style={{ display: 'none' }} multiple data-testid="instruction-files-input" />
                  <FileUp className="h-6 w-6 text-gray-400 mx-auto mb-2" />
                  <span className="text-sm text-gray-600">Click to upload</span>
                  <p className="text-xs text-gray-400 mt-1">DOCX, PDF, TXT, XLSX (max 10MB)</p>
                </div>
                {instructionFiles.length > 0 && (
                  <div className="mt-3 space-y-2" data-testid="uploaded-files-list">
                    {instructionFiles.map((file, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-2">{getFileIcon(file.name)}<span className="text-sm text-gray-700">{file.name}</span><span className="text-xs text-gray-400">({(file.size/1024).toFixed(1)} KB)</span></div>
                        <button type="button" onClick={() => setInstructionFiles(p => p.filter((_,idx) => idx !== i))} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg" data-testid={`remove-file-${i}`}><Trash2 size={14} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Workflow Steps */}
            <div className="apple-input-group mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="flex items-center gap-2 text-sm font-semibold text-gray-700"><Briefcase size={15} className="text-blue-600" /> Workflow Steps</span>
                {formData.workflow_steps.length < 6 && (
                  <button type="button" onClick={() => setFormData(p => ({ ...p, workflow_steps: [...p.workflow_steps, { step: p.workflow_steps.length + 1, name: `Step ${p.workflow_steps.length + 1}` }] }))} className="text-xs text-blue-600 hover:text-blue-800 font-medium px-2 py-1 rounded-lg hover:bg-blue-50">+ Add Step</button>
                )}
              </div>
              <div className="space-y-2">
                {formData.workflow_steps.map((s, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-blue-100 text-blue-700 text-xs font-bold">{s.step}</span>
                    <input className="apple-input flex-1 py-1.5 text-sm" value={s.name} placeholder={`Step ${s.step} name`}
                      onChange={e => setFormData(p => ({ ...p, workflow_steps: p.workflow_steps.map((ws, i) => i === idx ? { ...ws, name: e.target.value } : ws) }))} />
                    {formData.workflow_steps.length > 1 && (
                      <button type="button" onClick={() => setFormData(p => ({ ...p, workflow_steps: p.workflow_steps.filter((_,i) => i !== idx).map((ws,i) => ({ ...ws, step: i+1 })) }))} className="p-1 text-gray-400 hover:text-red-500 rounded"><X size={14} /></button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Translation Instructions */}
            <div className="apple-input-group">
              <button type="button" className="flex items-center justify-between w-full py-3 text-left" onClick={() => setShowInstructions(v => !v)} data-testid="toggle-instructions-btn">
                <span className="flex items-center gap-2 text-sm font-semibold text-gray-700"><BookOpen size={16} className="text-blue-600" /> Translation Instructions</span>
                {showInstructions ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
              </button>
              {showInstructions && (
                <div className="mt-4 space-y-5 border-t border-gray-100 pt-5">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 block">Pre-built Instructions</label>
                    <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto p-3 bg-gray-50 rounded-xl" data-testid="prebuilt-instructions-list">
                      {PREBUILT_INSTRUCTIONS.map((instr, idx) => (
                        <label key={idx} className="flex items-center gap-3 text-sm cursor-pointer hover:bg-white p-2.5 rounded-lg transition-colors">
                          <input type="checkbox" checked={formData.prebuilt_instructions.includes(instr)} onChange={() => handleInstructionToggle(instr)} className="w-4 h-4 rounded-md border-gray-300 text-blue-600" data-testid={`instruction-checkbox-${idx}`} />
                          <span className="text-gray-700">{instr}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 block">Custom Instructions</label>
                    <textarea value={formData.custom_instructions} onChange={e => setFormData(p => ({ ...p, custom_instructions: e.target.value }))} className="apple-input min-h-[100px] resize-none text-sm" placeholder="Add specific instructions..." data-testid="custom-instructions-input" />
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-100 mt-6">
              <ButtonApple type="button" variant="ghost" onClick={onClose} data-testid="cancel-btn">Cancel</ButtonApple>
              <ButtonApple type="submit" disabled={saving} data-testid="save-project-btn">
                {saving ? <><Loader2 size={16} className="animate-spin" />{project ? 'Updating...' : 'Creating...'}</> : project ? 'Update Project' : 'Create Project'}
              </ButtonApple>
            </div>
          </form>
        </div>
      </div>
      {showPDFDisclaimer && (
        <PDFDisclaimer isOpen={showPDFDisclaimer} fileName={pendingPDFFiles.length === 1 ? pendingPDFFiles[0]?.name : `${pendingPDFFiles.length} PDF files`} onProceed={handlePDFProceed} onCancel={handlePDFCancel} uploadContext="project-instructions" />
      )}
    </>
  );
};

export default ProjectsPageApple;
